/**
 * Import Dependency
 */
import { navigationConfig } from '@/config'

/**
 * Does the active user have access to a feature?
 *
 * @param {*} param0
 * @returns
 */
const securityCheck = ({ state, attribute }) => {

  let accepted = navigationConfig[attribute] || []

  if (! Array.isArray(accepted) || accepted.length === 0) {
    return false
  }

  return accepted.some(role => state.roles.includes(role))
}

/**
 * Does any user have access to a feature?
 *
 * @param {*} param0
 * @returns
 */
const someoneHasAccess = ({ attribute }) => {
  let accepted = navigationConfig[attribute] || []
  return (Array.isArray(accepted) && accepted.length !== 0)
}



/**
 * Import API
 */

/**
 * Declare Variable
 */
const state = {
  loaded: false,

  roles: [],

  roleDetails: {},


  /**
   * Remaining time on idToken in seconds
   */
  remainingTime: null,

  /**
   * Remaining time on a fresh idToken.
   */
  initialRemainingTime: null,
  /**
   * Load userSettings initially from local storage.
   */
  userSettings: JSON.parse(localStorage.getItem('userSettings')),
}

const getters = {

  rolesAreLoaded: state => !! state.loaded,

  // isAdmin: state => !! state.isAdmin,
  // isCPO: state => !! state.isCPO,
  // isMunicipality: state => !! state.isMunicipality,
  // isRVB: state => !! state.isRVB,
  // isCustomer: state => !! state.isCustomer,
  // isCaseviewer: state => !! state.isCaseviewer,
  // isCaseworker: state => !! state.isCaseworker,

  hasRole: state => ({ role }) => state.roles.includes(role),

  /**
   * A user is a guest when a user has no particular rights at all, or only the role "guest".
   */
  isGuest: state => state.roles.length === 0 || (state.roles.length === 1 && state.roles[0] === 'guest'),

  roleDetails: state => state.roleDetails,
  roleDetailsByRole: state => ({ role }) => state.roleDetails[role] || [],

  // Merge all role details that are in array format
  roleDetailsMerged: state => {
    return state.roles.reduce( (details, role) =>
      Array.isArray(state.roleDetails[role])
        ? details.concat(state.roleDetails[role])
        : details
    , [])
  },

  roleName: state => {

    let names = []

    if (state.roles.includes('municipality')) {
      names.push('gemeente')
    }
    if (state.roles.includes('cpo')) {
      names.push('CPO')
    }
    if (state.roles.includes('admin')) {
      names.push('beheerder')
    }

    if (state.roles.includes('rvb')) {
      names.push('RVB medewerker')
    }

    // RVB
    if (state.roles.includes('customer')) {
      names.push('klant')
    }
    if (state.roles.includes('caseworker')) {
      names.push('medewerker')
    }
    if (state.roles.includes('caseviewer')) {
      names.push('bezoeker')
    }


    // Doesn't actually exist, but we need a fallback that implies no rights are provided
    if (names.length === 0) {
      return 'bezoeker'
    }

    if (names.length === 1) {
      return names[0]
    }

    if (names.length === 2) {
      return names.join(' en ')
    }

    let last = names.pop()
    return `${names.join(', ')} en ${last}`
  },


  canAccessRequests: state => securityCheck({ state, attribute: 'requestlist' }),
  canAccessRequestLocations: state => securityCheck({ state, attribute: 'requestlocations' }),
  canAccessRealisationProcesses: state => securityCheck({ state, attribute: 'realisationlist' }),
  canAccessAssetManagement: state => securityCheck({ state, attribute: 'locationlist' }),
  canStartRealisationProcess: state => securityCheck({ state, attribute: 'realisationbtn' }),
  canPostRequestComment: state => securityCheck({ state, attribute: 'requestcomment' }),
  canChangeStatus: state => securityCheck({ state, attribute: 'canChangeStatus' }),
  canChangeCommentStatus: state => securityCheck({ state, attribute: 'canChangeCommentStatus' }),
  canModerateComments: state => securityCheck({ state, attribute: 'canModerateComments' }),
  canAutomateViews: state => securityCheck({ state, attribute: 'automatedViews' }),
  canGenerateStartDoc: state => securityCheck({ state, attribute: 'generateStartDoc' }),
  canAccessConfig: state => securityCheck({ state, attribute: 'admin' }),
  canAccessReporting: state => securityCheck({ state, attribute: 'reporting' }),
  canAccessEventLog: state => securityCheck({ state, attribute: 'eventlog' }),
  canConfirmTrafficDecisions: state => securityCheck({ state, attribute: 'canConfirmTrafficDecisions' }),
  canAccessExport: state => securityCheck({ state, attribute: 'export' }),

  // Disabled feature
  canAccessManuals: (state) => false && securityCheck({ state, attribute: 'manuals' }),

  securityCheck: state => ({ attribute }) => securityCheck({ state, attribute }),
  someoneHasAccess: () => ({ attribute }) => someoneHasAccess({ attribute }),

  /**
   * Remaining time on idToken in seconds
   */
  getRemainingTime: state => state.remainingTime,
  belowSessionWarningTreshold: state => state.remainingTime < 300, // 5 minutes
  getInitialRemainingTime: state => state.initialRemainingTime,
  getUserSettings: state => state.userSettings || {},
}
const actions = {
  saveUserSettings ({ commit }, { userSettings }) {
    commit('setUserSettings', userSettings)
  },
}
const mutations = {
  setRoles(state, { roles }) {
    // console.log("roles", roles)

    state.roles = Object
      .keys(roles)
      .filter(role => (Array.isArray(roles[role]) && roles[role].length !== 0) || roles[role] === true)

    // Raw role data
    // Account for Amsterdam permissions being stated simply as 'true'
    state.roleDetails = Object
      .keys(roles)
      .reduce((roleDetails, role) => {
        roleDetails[role] = roles[role] === true ? ['*'] : roles[role]
        return roleDetails
      }, {})

    state.loaded = true
  },
  /**
   * Remaining time on idToken in seconds
   */
  setRemainingTime(state, { remainingTime }) {
    if (! remainingTime && remainingTime !== 0) return

    state.remainingTime = remainingTime

    if (state.initialRemainingTime === null) {
      state.initialRemainingTime = remainingTime
    }
  },
  setUserSettings (state, userSettings) {
    state.userSettings = userSettings
  },
}
// Save userSettings to local storage on page unload (refresh, close browser, leave...) //
window.addEventListener('beforeunload', () => {
  if (localStorage.getItem('userSettings')) {
    localStorage.setItem('userSettings', JSON.stringify(state.userSettings))
  }
})

/**
 * Export
 */
export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
